<template>
  <BGModal :shown="dialogShown"
           max-width="90vw"
           :title="`${$t('admin_event.edit_title')} ${event ? '(' + $sanitizeHtml(event.name) + ')' : ''}`"
           fixed-tabs>
    <template #content>
      <v-container class="edit-event-content pa-0" fluid v-if="event">
        <v-tabs fixed-tabs
                v-model="tabIndex">
          <v-tab>{{ $t('register.basic_info') }}</v-tab>
          <v-tab-item eager>
            <EventInfo :event-id="eventId"
                       :save.sync="saveEvent"
                       v-if="tabIndex === 0"></EventInfo>
          </v-tab-item>

          <v-tab :disabled="event.signup_mode === -1 || event.signup_mode === 1">{{ $t('event.session') }}</v-tab>
          <v-tab-item>
            <v-row class="flex-grow-0" no-gutters>
              <v-col class="text-right">
                <v-spacer></v-spacer>
                <v-btn color="primary"
                       class="mr-4"
                       @click="addSessionModalShown = true">
                  {{ $t('admin_session.add_title') }}
                </v-btn>
              </v-col>
            </v-row>
            <SessionList full-height
                         hide-search
                         :event-id="eventId"
                         :refresh-trigger.sync="sessionRefreshTrigger"
                         v-if="tabIndex === 1"
                         @edit="editSession"></SessionList>

            <AddSessionModal :event-id="eventId"
                             :shown="addSessionModalShown"
                             @cancel="addSessionModalShown = false"
                             @finish="onAddSessionFinish"></AddSessionModal>

            <EditSessionModal :session-id="editingSessionId"
                              :shown="editSessionModalShown"
                              @cancel="editSessionModalShown = false"
                              @finish="onEditSessionFinish"></EditSessionModal>
          </v-tab-item>

          <v-tab :disabled="event.signup_mode !== 3">{{ $t('event.series') }}</v-tab>
          <v-tab-item>
            <v-row class="flex-grow-0" no-gutters>
              <v-col class="text-right">
                <v-spacer></v-spacer>
                <v-btn color="primary"
                       class="mr-4"
                       @click="addSeriesModalShown = true">
                  {{ $t('admin_series.add_title') }}
                </v-btn>
              </v-col>
            </v-row>

            <SeriesList full-height
                        hide-search
                        :event-id="eventId"
                        :refresh-trigger.sync="seriesRefreshTrigger"
                        v-if="tabIndex === 2"
                        @edit="editSeries"></SeriesList>

            <AddSeriesModal :event-id="eventId"
                            :shown="addSeriesModalShown"
                            @cancel="addSeriesModalShown = false"
                            @finish="onAddSeriesFinish"></AddSeriesModal>

            <EditSeriesModal :series-id="editingSeriesId"
                             :shown="editSeriesModalShown"
                             @cancel="editSeriesModalShown = false"
                             @finish="onEditSeriesFinish"></EditSeriesModal>
          </v-tab-item>

          <v-tab>{{ $t('admin_user.enrollment_history') }}</v-tab>
          <v-tab-item eager>
            <EventEnrollment :event-id="eventId"
                             ref="eventEnrollment"
                             v-if="tabIndex === 3"></EventEnrollment>
          </v-tab-item>

          <v-tab>{{ $t('admin_participant.title') }}</v-tab>
          <v-tab-item eager>
            <EventParticipant :event-id="eventId"
                              ref="eventParticipant"
                              v-if="tabIndex === 4"></EventParticipant>
          </v-tab-item>

          <v-tab>{{ $t('admin_payment.title') }}</v-tab>
          <v-tab-item>
            <div class="d-flex flex-column fill-height pa-4 pb-0">
              <v-row class="flex-grow-0">
                <v-col class="text-left">
                  <v-text-field outlined
                                :label="$t('search')"
                                v-model="paymentSearch"
                                dense
                                prepend-inner-icon="fas fa-search"></v-text-field>
                </v-col>
              </v-row>
              <PaymentList :event-id="eventId"
                           :fee-tiers="event.fee_tiers"
                           :search="paymentSearch"
                           v-if="tabIndex === 5"></PaymentList>
            </div>
          </v-tab-item>

          <v-tab>{{ $t('past_event.title') }}</v-tab>
          <v-tab-item eager>
            <EventMemorableMoment :event-id="eventId"
                                  :reset.sync="resetMemorableMoment"
                                  :save.sync="saveMemorableMoment"
                                  v-if="tabIndex === 6"></EventMemorableMoment>
          </v-tab-item>

          <v-tab>{{ $t('admin_hkiarace.email_template') }}</v-tab>
          <v-tab-item>
            <EventEmailTemplate :event-id="eventId"
                                :can-remove.sync="canRemoveEmailTemplate"
                                :remove.sync="removeEmailTemplate"
                                :changed.sync="emailTemplateChanged"
                                :save.sync="saveEmailTemplate"
                                v-if="tabIndex === 7"></EventEmailTemplate>
          </v-tab-item>

          <v-tab>{{ $t('admin_event.survey') }}</v-tab>
          <v-tab-item>
            <EventSurvey :event-id="eventId"
                         :save.sync="saveSurvey"
                         v-if="tabIndex === 8"></EventSurvey>
          </v-tab-item>

        </v-tabs>
      </v-container>
    </template>

    <template #footer>
      <v-row no-gutters>
        <v-btn @click="cancel"
               class="mr-3"
               depressed
               large>
          {{ $t('cancel') }}
        </v-btn>
        <v-spacer></v-spacer>

        <v-btn color="primary"
               depressed
               large
               @click="saveEvent = true"
               v-if="tabIndex === 0">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>

        <v-btn depressed
               class="ml-3"
               large
               @click="resetMemorableMoment"
               v-if="tabIndex === 6">
          {{ $t('admin_memorable_moment.revert') }}
          <v-icon right>fas fa-undo</v-icon>
        </v-btn>
        <v-btn color="primary"
               class="ml-3"
               depressed
               large
               @click="saveMemorableMoment"
               v-if="tabIndex === 6">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>

        <v-btn depressed
               class="ml-3"
               large
               :disabled="!canRemoveEmailTemplate"
               @click="removeEmailTemplate"
               v-if="tabIndex === 7">
          {{ $t('admin_notification_template.delete_template') }}
          <v-icon right>fas fa-trash</v-icon>
        </v-btn>
        <v-btn color="primary"
               class="ml-3"
               depressed
               large
               @click="saveEmailTemplate"
               :disabled="!emailTemplateChanged"
               v-if="tabIndex === 7">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>

        <v-btn color="primary"
               class="ml-3"
               depressed
               large
               @click="saveSurvey"
               v-if="tabIndex === 8">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>
      </v-row>
    </template>
  </BGModal>
</template>
<script>
import BGModal from "@/components/BGModal";
import {DateTime} from 'luxon';
import {ref, watch} from "@vue/composition-api";
import SessionList from "@/components/event/session/SessionList";
import SeriesList from "@/components/event/series/SeriesList";
import AddSessionModal from "@/components/event/session/AddSessionModal";
import EditSessionModal from "@/components/event/session/EditSessionModal";
import PaymentList from "@/components/event/PaymentList";
import EventSurvey from "@/components/event/EventSurvey";
import EventInfo from "@/components/event/EventInfo";
import EventEnrollment from "@/components/event/enrollment/EventEnrollment";
import EventParticipant from "@/components/event/participant/EventParticipant";
import EventMemorableMoment from "@/components/event/EventMemorableMoment";
import EventEmailTemplate from "@/components/event/EventEmailTemplate";
import AddSeriesModal from "@/components/event/series/AddSeriesModal";
import EditSeriesModal from "@/components/event/series/EditSeriesModal";

export default {
  name: 'EditEventModal',
  components: {EditSeriesModal, AddSeriesModal, EventEmailTemplate, EventMemorableMoment, EventParticipant, EventEnrollment, EventInfo, EventSurvey, PaymentList, EditSessionModal, AddSessionModal, SeriesList, SessionList, BGModal},
  props: {
    shown: Boolean,
    eventId: String,
  },
  setup(props, {root, emit, refs}) {
    const dialogShown = ref(props.shown);

    const tabIndex = ref(0);

    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);

    // Init
    watch(() => props.shown, async (newValue, oldValue) => {
      if (newValue !== oldValue) {
        dialogShown.value = newValue;

        if (newValue) {
          tabIndex.value = 0;
          // getCategories();
          getEvent();
          // getEmailTemplates();
        }
      }

      tabIndex.value = 0;
    });
    const cancel = function () {
      emit('cancel');
    };

    const getURL = function (f) {
      if (f instanceof File) {
        return URL.createObjectURL(f);
      } else {
        return 'api/static/' + f;
      }
    };

    const event = ref(null);
    const getEvent = function () {
      root.$callAPI('hkiarace/admin/event/', {id: props.eventId}, function (response) {
        event.value = {
          ...response.body.event,
          languages: JSON.parse(response.body.event.languages),
          from_time: response.body.event.from_time ? DateTime.fromFormat(response.body.event.from_time, 'HH:mm:ss').toFormat('HH:mm') : null,
          to_time: response.body.event.to_time ? DateTime.fromFormat(response.body.event.to_time, 'HH:mm:ss').toFormat('HH:mm') : null,
        };
      });
    };

    // Info
    const saveEvent = ref(false);

    // Sessions
    const sessionRefreshTrigger = ref(false);
    const addSessionModalShown = ref(false);
    const onAddSessionFinish = function () {
      addSessionModalShown.value = false;
      sessionRefreshTrigger.value = true;
    };

    const editSession = function (item) {
      editingSessionId.value = item.id;
      editSessionModalShown.value = true;
    }
    const editingSessionId = ref(null);
    const editSessionModalShown = ref(false);
    const onEditSessionFinish = function () {
      editingSessionId.value = null;
      editSessionModalShown.value = false;
      sessionRefreshTrigger.value = true;
    }

    // Series
    const seriesRefreshTrigger = ref(false);
    const addSeriesModalShown = ref(false);
    const onAddSeriesFinish = function () {
      addSeriesModalShown.value = false;
      seriesRefreshTrigger.value = true;
    };

    const editSeries = function (item) {
      editingSeriesId.value = item.id;
      editSeriesModalShown.value = true;
    }
    const editingSeriesId = ref(null);
    const editSeriesModalShown = ref(false);
    const onEditSeriesFinish = function () {
      editingSeriesId.value = null;
      editSeriesModalShown.value = false;
      seriesRefreshTrigger.value = true;
    }

    // Payment
    const paymentSearch = ref('');

    // Memorable Moment
    const resetMemorableMoment = null;
    const saveMemorableMoment = null;

    // Email Templates
    const canRemoveEmailTemplate = ref(false);
    const removeEmailTemplate = null;
    const emailTemplateChanged = ref(false);
    const saveEmailTemplate = null;

    // Survey
    const saveSurvey = null;

    return {
      dialogShown,
      tabIndex,
      requiredRules,

      cancel,

      getURL,
      event,

      // Info
      saveEvent,

      // Sessions
      sessionRefreshTrigger,
      addSessionModalShown,
      onAddSessionFinish,

      editSession,
      editingSessionId,
      editSessionModalShown,
      onEditSessionFinish,

      // Series
      seriesRefreshTrigger,
      addSeriesModalShown,
      onAddSeriesFinish,

      editSeries,
      editingSeriesId,
      editSeriesModalShown,
      onEditSeriesFinish,

      // Payment
      paymentSearch,

      // Memorable Moment
      resetMemorableMoment,
      saveMemorableMoment,

      // Email Templates
      canRemoveEmailTemplate,
      removeEmailTemplate,
      emailTemplateChanged,
      saveEmailTemplate,

      // Survey
      saveSurvey,
    }
  }
}
</script>

<style lang="less">
.edit-event-content {
  display: flex;
  height: 100%;

  .memorable-moment-list--table {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;

    .v-data-table__wrapper {
      flex: 1 1 0;
      overflow-y: auto;
    }

    .v-data-footer {
      margin-right: 0 !important;
    }
  }
}
</style>
